import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Box, Button, IconButton, Stack, Tooltip } from '@mui/material'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useMemo } from 'react'
import { useMutation } from 'urql'
import { useOrderContext } from '../../contexts/OrderContextProvider'
import { graphql } from '../../gql'
import { OrderContextProviderQuery } from '../../gql/graphql'
import BigStatCurrency from '../BigStatCurrency'
import DateDisplay from '../DateDisplay'

const deletePaymentMutation = graphql(/* GraphQL */ `
  mutation OrderTimelinePaymentsDeletePayment($deletePaymentId: Int!) {
    DeletePayment(id: $deletePaymentId) {
      id
    }
  }
`)

type Props = {
  po: NonNullable<NonNullable<Required<OrderContextProviderQuery['Rfq']['quote']>>['purchaseOrder']>
  onAddPaymentClick: () => void
}
export function Payments(props: Props) {
  const { openEditPaymentDialog } = useOrderContext()
  const { po, onAddPaymentClick } = props
  const sorted = useMemo(() => {
    return po.payments.slice().sort((a, b) => {
      const da = a.dateReceived || a.dateCreated
      const db = b.dateReceived || b.dateCreated
      return da.localeCompare(db)
    })
  }, [po.payments])

  const [deletePaymentResult, deletePayment] = useMutation(deletePaymentMutation)

  useEffect(() => {
    if (deletePaymentResult.error) {
      alert(deletePaymentResult.error)
    }
  }, [deletePaymentResult.error])

  const $subtotal = useMemo(() => {
    if (po.payments.length < 2) return null
    const v = po.payments.reduce((acc, p) => acc + p.amount, 0)
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scaleY: 0, maxHeight: 0 }}
        animate={{ opacity: 1, scaleY: 1, maxHeight: '10vh', transition: { duration: 0.3 } }}
        exit={{ opacity: 0, scaleY: 0, maxHeight: 0, transition: { duration: 0.3 } }}
        transition={{
          duration: 0.4,
          scale: { type: 'spring', visualDuration: 0.4, bounce: 0.5 },
        }}
        style={{ transformOrigin: 'center top' }}
      >
        <Box display="flex" flexDirection="row" justifyItems="flex-start">
          <Box flex="0 0 12em" textAlign="right" pr={4}>
            Subtotal
          </Box>
          <Box flex={1}>
            <BigStatCurrency value={v} sx={{ fontSize: '1.75em' }} maximumFractionDigits={2} color="success" />
          </Box>
        </Box>
      </motion.div>
    )
  }, [po.payments])

  return (
    <Stack spacing={4} mt={2}>
      {po.payments.length > 0 && (
        <Stack spacing={1}>
          <AnimatePresence initial={false}>
            {sorted.map((p) => (
              <motion.div
                key={p.id}
                layout
                initial={{ opacity: 0, scaleY: 0, maxHeight: 0 }}
                animate={{ opacity: 1, scaleY: 1, maxHeight: '10vh', transition: { duration: 0.3 } }}
                exit={{ opacity: 0, scaleY: 0, maxHeight: 0, transition: { duration: 0.3 } }}
                transition={{
                  duration: 0.4,
                  scale: { type: 'spring', visualDuration: 0.4, bounce: 0.5 },
                }}
                style={{ transformOrigin: 'center top' }}
              >
                <Box display="flex" flexDirection="row" justifyItems="flex-start">
                  <Box flex="0 0 12em" textAlign="right" pr={4}>
                    <strong>
                      <DateDisplay date={p.dateReceived || p.dateCreated} absolute format="LL" />
                    </strong>
                  </Box>
                  <Box flex={1}>
                    <BigStatCurrency
                      value={p.amount}
                      sx={{ fontSize: '1.75em' }}
                      maximumFractionDigits={2}
                      color="success"
                    />
                  </Box>
                  <Stack flex="0 1" direction="row" spacing={1}>
                    <Tooltip title="Edit Payment" placement="top" arrow>
                      <IconButton aria-label="edit" onClick={() => openEditPaymentDialog(p.id)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Payment" placement="top" arrow>
                      <IconButton aria-label="delete" onClick={() => deletePayment({ deletePaymentId: p.id })}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </motion.div>
            ))}
            {$subtotal}
          </AnimatePresence>
        </Stack>
      )}
      {!po.dateClosed && (
        <Button onClick={onAddPaymentClick} variant="contained" color="secondary">
          Add Payment
        </Button>
      )}
    </Stack>
  )
}
